import { AttachmentMenuItem, AttachmentMenuItemType, AttachmentSubmenu } from './attachments-menu'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useAttachments, useAttachmentStore } from '../../../../../store'
import { Timestamp } from '../audio/audio-elements'
import { useTrackAddAttachment, useTrackRemoveAttachment } from '../../../../../hooks/attachments-hooks'
import { useActions } from '../../../../../providers'
import { ClipboardHistoryItem } from '../../../../../types'
import { ClipboardList, Link2 } from 'lucide-react'
import { appIconUrl } from '@renderer/utils/app-icon'

export const ClipboardMenuItem = (item: AttachmentMenuItemType) => {
  const [items, setItems] = useState<ClipboardHistoryItem[]>([])
  const { getClipboardHistory } = useActions()

  useEffect(() => {
    getClipboardHistory().then((history) => setItems(history))
  }, [])

  return (
    <AttachmentSubmenu item={item}>
      {items.length === 0 && (
        <AttachmentMenuItem
          id={'not-available'}
          label={<span className={'leading-normal'}>No clipboard history</span>}
          disabled
          onClick={() => {}}
        />
      )}
      {items.map((item) => {
        return <ClipboardItem key={item.id} {...item} />
      })}
    </AttachmentSubmenu>
  )
}

const ClipboardItem = (item: ClipboardHistoryItem & { onClick?: (e: React.MouseEvent) => void }) => {
  const { addClipboardAttachment, removeAttachment } = useAttachmentStore()
  const attachments = useAttachments()
  const trackAddAttachment = useTrackAddAttachment()
  const trackRemoveAttachment = useTrackRemoveAttachment()
  const [failedSrcs, setFailedSrcs] = useState<Array<string>>([])

  const isLink = item.type === 'link'
  const embed = item.metadata?.pageMeta
  // Clipboard text keeps its original line breaks and indentation; collapse it so the
  // single-line row doesn't open with a gap. No slice — `truncate` ellipsizes at the real
  // column width instead of hard-cutting mid-word.
  const title = (item.title ?? item.content ?? '').replace(/\s+/g, ' ').trim()
  // Favicon, then the source app's icon, then the lucide placeholder below.
  const iconSrc = [isLink ? embed?.favicon : undefined, appIconUrl(item.app_name)].find(
    (src): src is string => !!src && !failedSrcs.includes(src),
  )

  const attachmentIndex = useMemo(() => {
    return attachments.findIndex((a) => a.attachment?.type === 'clipboardText' && a.attachment.text === item.content)
  }, [attachments, item])

  const onClick = useCallback<React.MouseEventHandler>(
    (e) => {
      e.stopPropagation()
      if (typeof item.onClick === 'function') {
        item.onClick(e)
      }
      if (attachmentIndex === -1) {
        addClipboardAttachment({ type: 'text', value: item.content! })
        trackAddAttachment({ type: 'clipboardText', trigger: 'Attachments Menu' })
      } else {
        removeAttachment(attachmentIndex)
        trackRemoveAttachment({ type: 'clipboardText', trigger: 'Attachments Menu' })
      }
    },
    [item, addClipboardAttachment, removeAttachment, trackAddAttachment, trackRemoveAttachment],
  )

  return (
    <AttachmentMenuItem
      id={`clipboard-${item.id}`}
      icon={
        iconSrc ? (
          <img
            src={iconSrc}
            alt=""
            className="size-3.5 shrink-0 rounded-sm object-contain"
            onError={() => setFailedSrcs((prev) => [...prev, iconSrc])}
          />
        ) : isLink ? (
          <Link2 />
        ) : (
          <ClipboardList />
        )
      }
      label={title}
      description={<Timestamp date={new Date(item.last_used_at)} />}
      selectable={true}
      selected={attachmentIndex !== -1}
      onClick={onClick}
    />
  )
}
